// Both ends of a live file transfer (docs/crypto-spec.md §6), on top of a
// HandoffConnection. The sender offers a manifest, waits for the recipient
// to accept, then streams chunks no more than WINDOW_BYTES ahead of what the
// recipient reports written. The recipient checks every chunk is the exact
// next one, writes it to its sink in order, and reports progress.

import type { HandoffRecordType } from '@sss/crypto';
import type { FileSink, SavedFile } from './fileSink';
import {
  CHUNK_BYTES,
  ChunkSequence,
  PROGRESS_EVERY_BYTES,
  WINDOW_BYTES,
  encodeChunk,
  encodeManifest,
  encodeProgress,
  parseChunk,
  parseProgress,
  totalBytes,
  type FileEntry,
} from './fileTransfer';

/** The slice of HandoffConnection a transfer needs (a fake in tests). */
export interface TransferChannel {
  send(type: Exclude<HandoffRecordType, 'signal'>, body?: Uint8Array): Promise<void>;
  sendBulk(type: 'chunk', body: Uint8Array): Promise<void>;
  notify(t: 'active'): void;
}

/** The room's deadline is pushed back this often while files move (the room can't see the data channel). */
const KEEP_ALIVE_MS = 60_000;

export const describeFiles = (files: readonly File[]): FileEntry[] => files.map((f) => ({ name: f.name, size: f.size, type: f.type }));

export class FileSender {
  readonly total: number;
  private acked = 0;
  private started = false;
  private stopped = false;
  private wake: (() => void) | null = null;
  private keepAlive: ReturnType<typeof setInterval> | undefined;

  constructor(
    private readonly channel: TransferChannel,
    private readonly files: readonly File[],
    /** Bytes the recipient has written so far. */
    private readonly onProgress: (written: number) => void,
  ) {
    this.total = totalBytes(describeFiles(files));
  }

  /** Offer the files. Nothing is sent until the recipient accepts. */
  async offer(): Promise<void> {
    await this.channel.send('manifest', encodeManifest(describeFiles(this.files)));
  }

  /** A `progress` record from the recipient: the first one starts the transfer. Returns false if it's invalid. */
  progress(body: Uint8Array): boolean {
    const written = parseProgress(body);
    if (written === null || written < this.acked || written > this.total) return false;
    this.acked = written;
    this.onProgress(written);
    this.wake?.();
    if (!this.started) {
      this.started = true;
      void this.run();
    }
    return true;
  }

  stop(): void {
    this.stopped = true;
    clearInterval(this.keepAlive);
    this.wake?.();
  }

  private async run(): Promise<void> {
    this.keepAlive = setInterval(() => this.channel.notify('active'), KEEP_ALIVE_MS);
    let sent = 0;
    try {
      for (const [index, file] of this.files.entries()) {
        for (let offset = 0; offset < file.size; offset += CHUNK_BYTES) {
          while (sent - this.acked >= WINDOW_BYTES && !this.stopped) await new Promise<void>((resolve) => (this.wake = resolve));
          if (this.stopped) return;
          const data = new Uint8Array(await file.slice(offset, offset + CHUNK_BYTES).arrayBuffer());
          await this.channel.sendBulk('chunk', encodeChunk(index, offset, data));
          sent += data.length;
        }
      }
    } finally {
      clearInterval(this.keepAlive);
    }
  }
}

export type ReceiveFailure = 'badChunk' | 'writeFailed';

export class FileReceiver {
  readonly total: number;
  private readonly sequence: ChunkSequence;
  private writes: Promise<void> = Promise.resolve();
  private written = 0;
  private reported = 0;
  /** Index of the next file to open. */
  private next = 0;
  private open = false;
  private failed = false;
  readonly saved: SavedFile[] = [];

  constructor(
    private readonly channel: TransferChannel,
    readonly files: readonly FileEntry[],
    private readonly sink: FileSink,
    private readonly events: { progress(written: number): void; done(saved: SavedFile[]): void; failed(reason: ReceiveFailure): void },
  ) {
    this.total = totalBytes(files);
    this.sequence = new ChunkSequence(files);
  }

  /** Tell the sender to start. Empty-only manifests finish at once. */
  async accept(): Promise<void> {
    await this.channel.send('progress', encodeProgress(0));
    if (this.sequence.done) this.enqueue(() => this.finish());
  }

  /** A `chunk` record. Anything out of order ends the transfer. */
  chunk(body: Uint8Array): void {
    if (this.failed) return;
    const chunk = parseChunk(body);
    if (!chunk || !this.sequence.accept(chunk)) {
      this.fail('badChunk');
      return;
    }
    const last = this.sequence.done;
    this.enqueue(async () => {
      await this.openThrough(chunk.fileIndex);
      await this.sink.write(chunk.data);
      this.written += chunk.data.length;
      this.events.progress(this.written);
      if (last) await this.finish();
      else if (this.written - this.reported >= PROGRESS_EVERY_BYTES) await this.report();
    });
  }

  /** Stop writing and discard the file in progress (the sender left, or we did). */
  async cancel(): Promise<void> {
    this.failed = true;
    await this.writes;
    await this.sink.abort();
  }

  private enqueue(work: () => Promise<void>): void {
    this.writes = this.writes.then(work).catch(() => this.fail('writeFailed'));
  }

  /** Close the open file and open each one up to `index` (creating any empty files in between). */
  private async openThrough(index: number): Promise<void> {
    while (this.next <= index) {
      if (this.open) this.saved.push(await this.sink.end());
      await this.sink.begin(this.files[this.next] as FileEntry);
      this.open = true;
      this.next += 1;
    }
  }

  private async report(): Promise<void> {
    this.reported = this.written;
    await this.channel.send('progress', encodeProgress(this.written));
  }

  private async finish(): Promise<void> {
    await this.openThrough(this.files.length - 1);
    if (this.open) this.saved.push(await this.sink.end());
    this.open = false;
    await this.report();
    await this.channel.send('delivered');
    this.events.done(this.saved);
  }

  private fail(reason: ReceiveFailure): void {
    if (this.failed) return;
    this.failed = true;
    void this.sink.abort();
    this.events.failed(reason);
  }
}
